import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { PieChart as PieIcon, AlertTriangle } from "lucide-react";
import api from "../api";
import { useUnit } from "../context/UnitContext";

const COLORS = ["#FF3B30", "#FF7A6E", "#C22E24", "#FFB0A8", "#8E241C", "#FF9587", "#5C1712", "#FFD3CF", "#7A1F18", "#FF6B5E"];

export const MuscleSplit = () => {
  const { unit, dw } = useUnit();
  const [data, setData] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    api.get("/volume-by-muscle").then(({ data }) => setData(data)).catch(() => {});
    api.get("/muscle-suggestions").then(({ data }) => setSuggestions(data)).catch(() => {});
  }, []);

  if (data.length === 0) return null;

  return (
    <section data-testid="muscle-split-section">
      <h2 className="font-display text-2xl text-white mb-2 flex items-center gap-2">
        RÉPARTITION DU VOLUME <PieIcon size={18} className="text-[#FF3B30]" />
      </h2>
      <div className="bg-[#14141A] border border-[#2E2E3A] p-3">
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="volume" nameKey="muscle" cx="50%" cy="50%"
                innerRadius={48} outerRadius={80} paddingAngle={2} stroke="#0A0A0A">
                {data.map((d, i) => <Cell key={d.muscle} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip
                formatter={(v, name, { payload }) => [`${Math.round(dw(v))} ${unit} (${payload.pct}%)`, payload.muscle]}
                contentStyle={{ background: "#1C1C1E", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div data-testid="muscle-split-legend" className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
          {data.map((d, i) => (
            <div key={d.muscle} className="flex items-center gap-2 text-xs">
              <span className="w-2.5 h-2.5 shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
              <span className="text-white truncate flex-1">{d.muscle}</span>
              <span className="text-[#B0B0B4] font-mono">{d.pct}%</span>
            </div>
          ))}
        </div>
        {suggestions.length > 0 ? (
          <div data-testid="muscle-suggestions" className="mt-3 space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-1.5">
              <AlertTriangle size={12} /> Muscles négligés (moins de 10 % du volume)
            </p>
            {suggestions.map((s) => (
              <div key={s.muscle} data-testid={`muscle-suggestion-${s.muscle}`}
                className="bg-[#14141A] border border-amber-400/20 px-3 py-2">
                <p className="text-white text-xs font-semibold">{s.muscle} <span className="text-amber-400 font-mono">· {s.pct} %</span></p>
                {s.exercices.length > 0 && (
                  <p className="text-[#B0B0B4] text-[11px] mt-0.5">
                    À rattraper : {s.exercices.map((e) => `${e.exercice} (Bloc ${e.bloc} · ${e.jour})`).join(" · ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#B0B0B4] text-[11px] mt-3">
            Aucun muscle sous les 10 % — belle répartition 💪
          </p>
        )}
      </div>
    </section>
  );
};
